import { useMemo } from "react";

import { deg2Rad } from "@/lib/utils";

interface Launch {
  angle: number;
  speed: number;
}

export interface LaunchDataset {
  name: string;
  color: string;
  icon: "circle" | "triangle";
  points: Launch[];
}

export interface LaunchVisualizationProps {
  datasets?: LaunchDataset[];
  maxSpeed: number;
  unit?: string;
}

export const RADAR_CENTER_X = 178.5;
export const RADAR_CENTER_Y = 178.5;
export const RADAR_RADIUS = 165.5;

export const getPointLocation = (
  angle: number,
  speed: number,
  maxSpeed: number,
): { x: number; y: number } => {
  /*
   * Perform a linear interpolation for `speed` between 0 and `maxSpeed` (`range`).
   *
   * The `offset` variable represents the clamped position in relation to the `range`.
   * The clamp is important to ensure that the `` value stays within the `range`.
   *
   * The `normalizedDistance` variable scales the position to a value between 0 and 1,
   * effectively converting the position into a percentage of the total `range`.
   *
   * Finally, we rescale this normalized value according to the distance from the center
   * to the outermost circle in the radar (`maxSpeed`), allowing the `speed` to adjust
   * proportionally to the size of the radar.*/

  /*
   * the range of angles goes from [-90; 90], no a normalization is need to bring it to [0; 359]
   * Multiplying by -1, so the "clock" goes anticlockwise
   */
  const normalizedAngle = (angle < 0 ? (angle % 360) + 360 : angle) * -1;

  const offset = Math.max(0, Math.min(speed, maxSpeed));

  if (maxSpeed === 0 || offset < 0) {
    return { x: RADAR_CENTER_X, y: RADAR_CENTER_Y };
  }
  const normalizedDistance = offset / maxSpeed;

  const distanceFromCenter = normalizedDistance * RADAR_RADIUS;

  return {
    x: RADAR_CENTER_X + Math.cos(deg2Rad(normalizedAngle)) * distanceFromCenter,
    y: RADAR_CENTER_Y + Math.sin(deg2Rad(normalizedAngle)) * distanceFromCenter,
  };
};

const LaunchVisualization = ({
  datasets = [],
  maxSpeed = 115,
  unit = "MPH",
}: LaunchVisualizationProps) => {
  const points = useMemo(() => {
    return datasets.reduce<
      { x: number; y: number; color: string; icon: "circle" | "triangle" }[]
    >((acc, current) => {
      return acc.concat(
        current.points.map((point) => {
          const coords = getPointLocation(point.angle, point.speed, maxSpeed);
          return {
            ...coords,
            color: current.color,
            icon: current.icon,
          };
        }),
      );
    }, []);
  }, [maxSpeed, datasets]);

  return (
    <svg
      width="357"
      height="357"
      viewBox="0 -5 380 380"
      version="1.1"
      id="svg1"
      xmlSpace="preserve"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs id="defs1" />
      <g id="layer1">
        <g id="g3" transform="matrix(1.0005856,0,0,1,122.45732,3.4005134e-5)">
          <rect
            style={{
              opacity: 0.5,
              fill: "none",
              stroke: "none",
              strokeWidth: 1.445,
            }}
            id="rect1"
            width="356.06857"
            height="355.31555"
            x="-122.38565"
            y="0.84219491"
          />
          <g
            id="g1"
            transform="matrix(3.5700002,0,0,3.5700002,-179.22134,-391.34223)"
          >
            <path
              id="path41"
              style={{
                fill: "#f47678",
                fillOpacity: 1,
                stroke: "#000000",
                strokeWidth: 0.181999,
                strokeDasharray: "none",
              }}
              d="m 65.930931,112.76589 v 93.66359 c 25.661483,-0.24387 46.390999,-21.11267 46.390999,-46.83179 0,-25.71913 -20.729516,-46.58793 -46.390999,-46.8318 z"
            />
            <path
              id="path41-0"
              style={{
                opacity: 1,
                fill: "#6c5aaf",
                fillOpacity: 1,
                strokeWidth: 0.19018,
              }}
              d="m 65.930931,136.08987 v 47.01562 c 12.830784,-0.12241 23.195565,-10.59777 23.195565,-23.50781 0,-12.91004 -10.364781,-23.38539 -23.195565,-23.50781 z"
            />
            <path
              style={{ fill: "#000000", strokeWidth: 0.242785 }}
              d="m 59.721053,169.83582 c -0.345983,0.008 -0.676404,-0.11596 -1.00861,-0.1599 -0.20482,0.12975 -0.623053,-0.15322 -0.231269,-0.15826 0.258583,-0.0327 0.509411,0.0907 0.764333,0.0616 -0.256865,-0.0809 -0.504095,-0.20533 -0.778357,-0.20739 0.367775,-0.019 0.164596,-0.23292 -0.06002,-0.29516 -0.23221,-0.11786 0.06744,-0.48317 -0.08048,-0.51497 -0.257167,-0.01 -0.25679,-0.38587 -0.430932,-0.53911 -0.152995,-0.52041 -0.486012,-0.97349 -0.788239,-1.4196 -0.155977,-0.21563 -0.03839,-0.54041 -0.28863,-0.68273 -0.173112,-0.26365 -0.333071,-0.5587 -0.638328,-0.68835 -0.05968,-0.43331 -0.0434,-0.90149 -0.161841,-1.31246 -0.323965,-0.12963 -0.01401,-0.38002 -0.158078,-0.6002 -0.133972,-0.14762 -0.230987,-0.44101 -0.400411,-0.48886 -0.300593,0.17398 -0.213371,0.10147 -0.199738,0.008 -0.178112,0.0891 -0.527886,0.0777 -0.598817,0.25098 0.0027,0.30887 0.378797,0.18342 0.572018,0.31426 0.333367,0.14436 -0.182184,-0.0212 -0.303656,-0.0213 -0.331506,-0.0839 -0.04409,0.31656 -0.0546,0.49167 0.148697,0.80847 0.354822,1.61653 0.393811,2.43853 -0.11788,0.28594 0.238302,0.28828 0.370034,0.46894 0.228886,0.19222 0.0353,-0.26796 -0.01518,-0.36389 -0.422312,0.0307 -0.131197,-0.13001 0.07371,-0.26757 -0.297001,-0.0641 -0.438774,-0.0589 -0.03647,-0.12392 0.323614,0.0312 0.376456,-0.20756 0.400205,-0.47536 0.06963,-0.48917 0.05563,0.0519 0.03647,0.25085 -0.04897,0.47536 0.0014,1.04423 -0.343737,1.41524 -0.450038,0.28211 -1.014708,0.27877 -1.500844,0.48409 -0.592979,0.13578 -1.180704,0.30966 -1.752112,0.50226 -0.207863,0.0518 -0.457874,0.11692 -0.599236,-0.0944 0.08413,0.2142 -0.174025,0.35586 -0.290174,0.39251 -0.02321,0.25889 -0.321094,0.43667 -0.249957,0.71284 -0.216688,0.13861 -0.389099,0.0323 -0.541628,-0.13875 -0.151478,-0.16321 -0.402025,-0.39383 -0.196476,-0.0563 0.158586,0.17726 0.354595,0.3537 0.57695,0.42696 -0.04766,-0.19968 0.40846,0.0336 0.410218,0.10444 -0.167686,0.10114 -0.442628,0.0128 -0.646611,-0.006 -0.40346,-0.20004 -0.531875,-0.68621 -0.539018,-1.10439 -0.153854,-0.2707 -0.315943,-0.57358 -0.159754,-0.88287 0.143889,-0.14474 0.236173,0.22181 0.211016,-0.14013 0.220527,-0.0961 0.468581,-0.0688 0.69632,-0.12952 -0.08089,0.15148 -0.319597,0.56714 0.0136,0.49379 -0.07417,-0.20263 0.0226,-0.42938 0.180914,-0.58584 -0.117036,-0.22609 0.382396,0.0626 0.08887,0.19459 -0.313511,0.0121 -0.05268,0.54707 -0.03557,0.33194 -0.09358,-0.32427 0.241495,0.003 0.297269,0.12838 -0.122097,-0.24751 -0.106445,-0.34969 0.213186,-0.3236 1.016603,-0.15566 1.977014,-0.5531 2.969649,-0.80896 0.442765,-0.0378 -0.139768,-0.27415 0.230245,-0.21096 0.103781,-0.40033 -0.08572,-0.85091 -0.137522,-1.26437 -0.136884,-0.59948 -0.187315,-1.22069 -0.406056,-1.79856 -0.214738,0.0465 -0.428274,0.23529 -0.561058,0.37 0.382342,-0.29242 0.0021,0.11966 0.181868,0.0524 0.231125,-0.0974 0.550605,-0.0982 0.151052,0.042 -0.289556,0.16494 -0.284659,0.20956 0.04409,0.17106 0.301157,-0.007 -0.535619,0.15288 -0.180357,0.16334 0.255876,0.0188 0.572018,0.0198 0.15463,0.0869 -0.391888,0.0834 0.07056,0.0854 0.225019,0.0987 0.361326,0.0439 -0.502811,0.062 -0.266727,0.11919 0.104968,0.0349 0.56434,0.22037 0.245416,0.14514 -0.211496,-0.0487 -0.595919,0.10615 -0.541683,-0.22345 -0.113134,-0.27086 -0.432408,-0.39003 -0.564745,-0.64507 -0.203551,-0.20254 -0.441275,-0.49469 -0.404244,-0.79328 0.201346,-0.12914 0.423776,-0.0691 0.296727,0.1815 0.114378,-0.0721 0.598274,-0.1765 0.419167,0.0716 -0.05652,0.18484 -0.376765,0.28449 -0.243844,0.48868 0.296534,-0.23811 0.543991,-0.56122 0.852736,-0.79814 -0.02445,-0.4026 -0.243116,-0.0472 -0.491692,-0.12828 -0.273705,-0.0562 0.424174,-0.2299 0.331891,-0.38795 -0.03702,-0.29764 -0.415878,-0.35354 -0.65944,-0.36329 -0.185138,0.12804 -0.365612,0.33925 -0.487207,0.52717 -0.01964,0.26716 -0.09774,0.39928 -0.09858,0.0506 -0.0027,-0.29267 0.110676,-0.63647 -0.01889,-0.8974 -0.197863,-0.14349 -0.511938,-0.20825 -0.493292,-0.51442 -0.05597,-0.33893 -0.119041,-0.67265 -0.270381,-0.98475 0.0454,-0.33978 -0.202431,-0.66002 -0.03722,-0.98975 4.53e-4,-0.469 0.26113,-0.91221 0.201655,-1.39169 -0.04155,-0.44815 0.251412,0.23984 0.162404,-0.13073 -0.21197,-0.13229 -0.05577,-0.37401 0.03681,-0.54452 0.125181,-0.24872 0.282634,-0.49914 0.543709,-0.62124 -0.192829,0.23745 -0.444406,0.48187 -0.507227,0.78007 0.261254,0.0483 0.478004,0.27667 0.738888,0.35073 -0.187095,-0.19054 -0.331252,-0.41693 -0.531334,-0.59612 0.287675,0.003 0.561978,0.14243 0.700812,0.40084 0.136087,0.11376 0.491836,0.3836 0.221771,0.0479 -0.132289,-0.14773 -0.176209,-0.61969 0.0386,-0.26533 0.267566,0.11531 -0.03564,-0.20712 -0.08781,-0.29498 -0.07817,-0.22267 0.286349,-0.41224 -0.02067,-0.50509 -0.162095,-0.13588 0.338463,0.16004 0.373674,-0.0374 -0.02417,-0.13041 0.26177,-0.51556 0.209677,-0.37447 -0.0579,0.2015 -0.293176,0.50118 -0.01346,0.63319 0.251027,0.22041 -0.117132,-0.24259 0.0048,-0.26237 0.04471,0.11273 0.487413,0.38062 0.244929,0.0848 -0.287771,-0.1753 -0.151965,-0.2571 0.09424,-0.0951 0.03537,0.30347 0.354444,0.14182 0.517522,0.28149 0.122056,-0.0686 0.208715,-0.37321 0.411729,-0.39453 0.200501,0.128 0.300463,-0.21758 0.0862,-0.29166 -0.133614,-0.20068 -0.407272,-0.0615 -0.540783,-0.2411 -0.171965,-0.0575 0.34042,0.007 0.42611,0.0833 0.281494,0.11052 0.441296,0.36985 0.441742,0.66869 0.02397,0.23514 0.05439,0.46756 0.298677,0.57157 0.194079,0.11545 0.417423,0.37965 0.06827,0.45017 -0.1759,0.15883 -0.489007,0.0641 -0.635238,0.15696 0.08932,0.2983 -0.380652,0.14017 -0.140221,0.41943 0.303869,0.27921 0.761634,0.26726 1.125425,0.44054 0.397588,0.157 0.855401,0.20935 1.174771,0.51713 0.196778,-0.34282 0.156745,0.003 0.278602,0.0832 0.09399,-0.26272 0.19182,0.1026 0.388928,0.13087 0.222038,0.10975 0.321362,0.26547 0.591826,0.22205 0.236042,0.13089 0.419531,-0.15844 0.503841,-0.0964 -0.08849,0.15366 -0.03626,0.27231 0.25282,0.20751 1.342532,-0.026 2.680168,-0.15443 4.023538,-0.16088 0.556278,-0.0323 1.119553,0.007 1.670907,-0.0754 0.19855,-0.33206 0.266288,0.0713 0.133573,0.23828 -0.147948,0.20421 -0.456061,0.0524 -0.674254,0.10051 -1.764412,0.0121 -3.535706,0.004 -5.295833,0.0285 -0.06058,0.17747 -0.219332,0.42524 -0.431145,0.45257 -0.26091,-0.0179 -0.50575,0.11128 -0.759127,0.0406 -0.742335,0.22216 -1.533885,0.65017 -2.312379,0.32978 -0.2387,-0.14325 -0.458197,-0.0333 -0.66558,0.10617 -0.17967,0.1613 0.03839,0.46901 -0.0511,0.65044 0.05213,0.19711 0.122626,0.42656 0.290244,0.58007 0.05007,0.33346 0.283636,0.55693 0.542218,0.74835 0.411606,0.43096 0.828273,0.89372 1.118585,1.40241 -0.3113,0.0835 -0.734966,0.20036 -0.794455,0.56921 0.176161,0.23128 -0.327488,0.43387 0.03571,0.38284 0.225802,-0.062 0.5277,-0.10396 0.218941,0.1464 -0.333546,0.33422 0.0014,0.0975 0.207815,-0.036 0.09319,0.0131 -0.286968,0.49083 -0.218522,0.55407 0.213392,0.0816 0.150811,-0.0253 0.234751,-0.28182 0.120153,-0.28224 0.381229,-0.51492 0.584772,-0.70323 -0.04416,0.31469 -0.401654,0.51472 -0.449496,0.85093 -0.09125,0.22144 0.200082,0.3686 -0.0592,0.51789 -0.06531,0.24618 0.05872,0.40863 0.172645,0.29421 0.190405,0.13905 0.365082,0.31453 0.468059,0.5292 -0.388385,-0.20767 -0.0893,0.10194 0.03922,0.25271 0.132543,0.18545 0.383839,0.20873 0.556943,0.33738 -0.234304,0.0277 -0.57855,0.20855 -0.449811,0.48518 0.191435,-0.0501 0.272098,0.0408 0.50483,0.0338 -0.203282,0.18814 -0.289749,0.33646 0.06312,0.1468 0.177795,-0.0791 0.570947,-0.038 0.626151,0.0253 -0.166759,0.1202 -0.415067,0.22261 -0.480305,0.41972 0.259695,0.0816 0.533023,-0.10682 0.734677,-0.24227 0.232588,0.11135 0.316335,0.40774 0.562026,0.50988 -0.108258,0.0606 -0.513017,0.2396 -0.381909,0.31634 0.314281,-0.15523 0.33656,-0.10321 0.133519,0.14428 0.224085,0.001 0.448383,-0.0349 0.63729,-0.12288 0.17226,0.10212 0.535702,-0.061 0.575501,0.17193 -0.0021,0.3771 -0.488649,0.43403 -0.78188,0.43052 -0.147667,0.011 -0.294179,0.001 -0.441419,-0.011 z m -4.027804,-2.85462 c -0.119714,-0.13433 -0.410129,-0.43352 -0.540385,-0.17963 0.171697,0.0933 0.374945,0.19383 0.571147,0.23906 l -0.0193,-0.0372 z m -0.819385,-4.16221 c 0.124109,-0.0975 0.675545,-0.0267 0.520379,-0.2092 -0.155214,-0.10464 -0.346107,-0.42252 -0.533573,-0.26813 -0.124363,0.1228 -0.494068,0.19994 -0.33542,0.41956 0.0215,0.23777 0.20853,0.0591 0.348614,0.0578 z m -1.138234,-1.04538 c 0.08154,-0.0787 0.623191,-0.30273 0.250361,-0.19706 -0.136224,0.0122 -0.583501,0.38426 -0.375549,0.27499 0.04176,-0.026 0.08346,-0.0519 0.125188,-0.0779 z m -0.335482,-1.00033 c -0.120112,-0.0318 -0.586949,-0.25375 -0.419902,-0.24818 0.197026,0.031 0.416447,0.0776 0.605898,0.0669 -0.472757,-0.14401 -0.874425,-0.44221 -1.304176,-0.67119 0.190975,0.40855 0.510833,0.78598 0.981976,0.85199 0.08952,0.0394 0.419559,0.0971 0.172638,0.0141 z m 2.557075,-0.57637 c 0.234428,-0.0301 0.215404,-0.5592 0.07582,-0.30122 2.41e-4,0.0611 -0.307626,0.46785 -0.07582,0.30122 z m -2.002061,-0.204 c -0.02795,-0.3974 0.412869,-0.61184 0.696464,-0.8073 0.25234,-0.19508 0.599579,-0.29639 0.791482,-0.54752 -0.112606,-0.0623 -0.470903,0.21298 -0.537089,-0.12046 -0.230204,-0.26183 -0.51632,-0.57726 -0.848087,-0.6251 0.22173,0.26829 -0.27922,-0.0183 -0.411866,-0.0305 -0.348352,-0.11255 0.245815,0.18812 0.346196,0.25372 0.210102,0.11817 0.567383,0.28825 0.114419,0.14719 -0.295882,-0.0631 -0.606255,-0.17911 -0.893319,-0.21014 0.274338,0.17265 0.58355,0.32651 0.84457,0.49657 -0.471232,-0.17859 -0.94833,-0.35284 -1.38751,-0.60221 0.408419,0.45608 1.077886,0.56363 1.567195,0.86289 -0.369252,-0.10265 -0.779113,-0.1919 -1.131414,-0.34686 -0.139459,-0.2632 -0.494103,-0.11992 -0.523655,0.14416 -0.05515,0.12485 -0.346697,0.68899 -0.129817,0.33972 0.135648,-0.19729 0.208928,-0.4944 0.496239,-0.49521 0.192815,0.17046 0.296582,0.38363 0.307481,0.64138 0.132866,0.33455 -0.296225,0.21228 -0.246028,-0.002 0.05735,-0.25896 -0.297482,-0.386 -0.350282,-0.0794 0.245904,0.43591 0.654852,0.77225 1.076533,1.0418 0.161622,-0.23758 -0.227423,-0.34555 -0.339266,-0.51415 -0.174836,-0.1444 0.07456,-0.44599 0.05412,-0.0741 0.117915,0.0713 0.590542,0.33581 0.362411,0.0569 -0.365384,-0.20476 -0.16511,-0.23196 0.152701,-0.23042 0.41335,-0.12195 0.137399,0.16272 0.01277,0.33598 -0.0863,0.14678 -0.198021,0.45987 -0.02418,0.54731 0,-0.0607 0,-0.12136 0,-0.18203 z m 3.265194,0.12332 c 0.269468,-0.19763 0.614235,0.10792 0.900846,-0.0874 0.210198,-0.0601 0.406763,-0.1745 0.617154,-0.22381 0.157357,0.17013 -0.08101,-0.27634 -0.28135,-0.0626 -0.24942,0.1011 -0.500015,-0.01 -0.650793,-0.1397 -0.162816,-0.0958 -0.357795,-0.29172 -0.604235,-0.31622 -0.503278,-0.19767 -1.074109,-0.13055 -1.550836,-0.4113 -0.314665,-0.0703 -0.534684,0.26288 -0.787457,0.40305 -0.294556,0.19988 -0.317646,0.26078 0.0062,0.0507 0.244276,-0.15402 0.508401,-0.272 0.763831,-0.40579 0.08362,0.26217 0.208234,0.27813 0.375048,0.33497 0.2032,0.18188 0.376696,0.37439 0.522027,0.60651 0.174856,0.20862 0.451007,-0.0448 0.672111,-0.0394 0.07174,0.0646 -0.160688,0.45211 0.01724,0.29082 z m -2.055425,-0.61218 c 0.372315,-0.032 -0.394978,-0.0627 0.07566,-0.16117 0.273849,0.01 0.523607,-0.0453 0.112337,-0.0928 0.206518,-0.16173 0.335853,-0.25226 -0.02726,-0.11073 -0.187699,0.0908 -0.354829,0.22377 -0.494576,0.37795 0.111664,-1.9e-4 0.223013,0.004 0.333813,-0.0132 z m -0.614022,-2.47472 c -0.250534,-0.097 -0.271,0.0549 0.0169,0.0525 0.02349,-0.0133 -0.01071,-0.0349 -0.0169,-0.0525 z"
              id="path40"
            />
            <path
              style={{
                fill: "none",
                stroke: "#000000",
                strokeWidth: 0.181999,
                strokeLinecap: "butt",
                strokeLinejoin: "miter",
                strokeDasharray: "none",
                strokeOpacity: 1,
              }}
              d="M 112.08191,159.67582 H 65.622262 l 32.954123,-32.95413"
              id="path1"
            />
            <text
              xmlSpace="preserve"
              style={{
                fontSize: 3.6,
                lineHeight: 1.25,
                letterSpacing: 0,
                strokeWidth: 0.181712,
              }}
              x="66.209671"
              y="116.85372"
              id="text1"
            >
              <tspan
                id="tspan1"
                style={{
                  fontStyle: "normal",
                  fontVariant: "normal",
                  fontWeight: "normal",
                  fontStretch: "normal",
                  fontSize: 3.6,
                  strokeWidth: 0.181712,
                }}
                x="66.209671"
                y="116.85372"
              >
                90º
              </tspan>
            </text>
            <text
              xmlSpace="preserve"
              style={{
                fontSize: 3.6,
                lineHeight: 1.25,
                letterSpacing: 0,
                strokeWidth: 0.181712,
              }}
              x="66.209656"
              y="111.93188"
              id="text1-1"
            >
              <tspan
                id="tspan1-4"
                style={{
                  fontStyle: "normal",
                  fontVariant: "normal",
                  fontWeight: "normal",
                  fontStretch: "normal",
                  fontSize: 3.6,
                  strokeWidth: 0.181712,
                }}
                x="66.209656"
                y="111.93188"
              >
                {`${maxSpeed} ${unit}`}
              </tspan>
            </text>
            <text
              xmlSpace="preserve"
              style={{
                fontSize: 3.6,
                lineHeight: 1.25,
                letterSpacing: 0,
                strokeWidth: 0.181712,
              }}
              x="66.018005"
              y="209.58594"
              id="text1-1-9"
            >
              <tspan
                id="tspan1-4-5"
                style={{
                  fontStyle: "normal",
                  fontVariant: "normal",
                  fontWeight: "normal",
                  fontStretch: "normal",
                  fontSize: 3.6,
                  strokeWidth: 0.181712,
                }}
                x="66.018005"
                y="209.58594"
              >
                {`${maxSpeed} ${unit}`}
              </tspan>
            </text>
            <text
              xmlSpace="preserve"
              style={{
                fontSize: 3.6,
                lineHeight: 1.25,
                letterSpacing: 0,
                strokeWidth: 0.181712,
              }}
              x="96.333046"
              y="124.02389"
              id="text1-1-8"
            >
              <tspan
                id="tspan1-4-9"
                style={{
                  fontStyle: "normal",
                  fontVariant: "normal",
                  fontWeight: "normal",
                  fontStretch: "normal",
                  fontSize: 3.6,
                  strokeWidth: 0.181712,
                }}
                x="96.333046"
                y="124.02389"
              >
                {`${maxSpeed} ${unit}`}
              </tspan>
            </text>
            <text
              xmlSpace="preserve"
              style={{
                fontSize: 3.6,
                lineHeight: 1.25,
                letterSpacing: 0,
                strokeWidth: 0.181712,
              }}
              x="66.41777"
              y="204.63789"
              id="text1-2"
            >
              <tspan
                id="tspan1-0"
                style={{
                  fontStyle: "normal",
                  fontVariant: "normal",
                  fontWeight: "normal",
                  fontStretch: "normal",
                  fontSize: 3.6,
                  strokeWidth: 0.181712,
                }}
                x="66.41777"
                y="204.63789"
              >
                -90º
              </tspan>
            </text>
            <text
              xmlSpace="preserve"
              style={{
                fontSize: 3.6,
                lineHeight: 1.25,
                letterSpacing: 0,
                strokeWidth: 0.181712,
              }}
              x="112.66944"
              y="160.91496"
              id="text1-2-4"
            >
              <tspan
                id="tspan1-0-7"
                style={{
                  fontStyle: "normal",
                  fontVariant: "normal",
                  fontWeight: "normal",
                  fontStretch: "normal",
                  fontSize: 3.6,
                  strokeWidth: 0.181712,
                }}
                x="112.66944"
                y="160.91496"
              >
                0º
              </tspan>
            </text>
            <text
              xmlSpace="preserve"
              style={{
                fontSize: 3.6,
                lineHeight: 1.25,
                letterSpacing: 0,
                strokeWidth: 0.181712,
              }}
              x="99.483009"
              y="127.07687"
              id="text1-2-4-8"
            >
              <tspan
                id="tspan1-0-7-6"
                style={{
                  fontStyle: "normal",
                  fontVariant: "normal",
                  fontWeight: "normal",
                  fontStretch: "normal",
                  fontSize: 3.6,
                  strokeWidth: 0.181712,
                }}
                x="99.483009"
                y="127.07687"
              >
                45º
              </tspan>
            </text>
          </g>
        </g>
      </g>
      <g>
        {points.map((p, index) => {
          const markerSize = 3.5;

          if (p.icon === "circle") {
            return (
              <circle
                cx={p.x}
                cy={p.y}
                r={markerSize}
                fill={p.color}
                key={`point-${index}`}
                role="img"
                data-testid={`point-${index}`}
              />
            );
          }

          return (
            <polygon
              points={`${p.x},${p.y - markerSize} ${p.x - markerSize},${p.y + markerSize} ${p.x + markerSize},${p.y + markerSize}`}
              fill={p.color}
              key={`point-${index}`}
              role="img"
              data-testid={`point-${index}`}
            />
          );
        })}
      </g>
    </svg>
  );
};

export default LaunchVisualization;
